import { useMutation, useQuery, useQueryCache } from "react-query";
import useNetworkHeaders from "../../hooks/useNetworkHeaders";
import { getCrmCustomListViews } from "../queries/getCrmCustomListViews";
import { postCrmCustomListView, PostCrmCustomListViewParamsType } from "../queries/postCrmCustomListView";
import { useToasts } from "react-toast-notifications";
import { putCrmCustomListView, PutCrmCustomListViewParamsType } from "../queries/putCrmCustomListView";
import { deleteCrmCustomListView } from "../queries/deleteCrmCustomListView";
import { useState } from "react";
import extractErrorInfo from "../../utils/extractErrorInfo";

export default function useCrmCustomListViews() {
  const headers = useNetworkHeaders();
  const queryCache = useQueryCache();
  const { addToast } = useToasts();
  const { isLoading: isCrmCustomListViewsLoading, data: crmCustomListViews } = useQuery("CrmCustomListViews", {
    queryFn: getCrmCustomListViews(headers),
  });

  const [isCreateCustomListViewMutationLoading, setIsCreateCustomListViewMutationLoading] = useState(false);
  const [createCustomListViewMutation] = useMutation(
    (body: PostCrmCustomListViewParamsType) => {
      setIsCreateCustomListViewMutationLoading(true);
      return postCrmCustomListView(headers)(body);
    },
    {
      onSuccess: async () => {
        await queryCache.invalidateQueries("CrmCustomListViews");
        addToast("Custom view created successfully", {
          appearance: "success",
          autoDismiss: true,
        });
      },
      onError: (error) => {
        const { title, subHeader, message } = extractErrorInfo(error);
        const errorMessage = `${title} ${subHeader} ${message}`;
        addToast(errorMessage, {
          appearance: "error",
          autoDismiss: true,
        });
      },
      onSettled() {
        setIsCreateCustomListViewMutationLoading(false);
      },
    },
  );
  const [isUpdateCustomListViewMutationLoading, setIsUpdateCustomListViewMutationLoading] = useState(false);
  const [updateCustomListViewMutation] = useMutation(
    ({ id, body }: { id: number; body: PutCrmCustomListViewParamsType }) => {
      setIsUpdateCustomListViewMutationLoading(true);
      return putCrmCustomListView(headers)({
        id,
        body,
      });
    },
    {
      onSuccess: async () => {
        await queryCache.invalidateQueries("CrmCustomListViews");
        addToast("Custom view updated successfully", {
          appearance: "success",
          autoDismiss: true,
        });
      },
      onError: (error) => {
        const { title, subHeader, message } = extractErrorInfo(error);
        const errorMessage = `${title} ${subHeader} ${message}`;
        addToast(errorMessage, {
          appearance: "error",
          autoDismiss: true,
        });
      },
      onSettled: () => {
        setIsUpdateCustomListViewMutationLoading(false);
      },
    },
  );

  const [isDeleteCustomListViewMutationLoading, setIsDeleteCustomListViewMutationLoading] = useState(false);
  const [deleteCustomListViewMutation] = useMutation(
    (id: number) => {
      setIsDeleteCustomListViewMutationLoading(true);
      return deleteCrmCustomListView(headers)(id);
    },
    {
      onSuccess: async () => {
        await queryCache.invalidateQueries("CrmCustomListViews");
        addToast("Custom view deleted successfully", {
          appearance: "success",
          autoDismiss: true,
        });
      },
      onError: (error) => {
        const { title, subHeader, message } = extractErrorInfo(error);
        const errorMessage = `${title} ${subHeader} ${message}`;
        addToast(errorMessage, {
          appearance: "error",
          autoDismiss: true,
        });
      },
      onSettled: () => {
        setIsDeleteCustomListViewMutationLoading(false);
      },
    },
  );

  return {
    isCrmCustomListViewsLoading,
    crmCustomListViews,
    createCustomListViewMutation,
    isCreateCustomListViewMutationLoading,
    updateCustomListViewMutation,
    isUpdateCustomListViewMutationLoading,
    deleteCustomListViewMutation,
    isDeleteCustomListViewMutationLoading,
  };
}
